import { Badge, Text } from "@chakra-ui/react"
import { Tooltip } from "../chakra/Tooltip"
import { FaCircleExclamation, FaTriangleExclamation, FaCircleInfo, FaCheck } from "react-icons/fa6"
import { toUserTZ } from "@hydrologiq/common/helpers/date"

export type AlertSeverityType = "critical" | "warning" | "info" | "ok"
export type AlertType = "status" | "pad" | "maintenance" | "anomaly" | "performance" | "optimisation" | "pre_emptive" | "reactive"

interface AlertSeverityBadgeProps {
  severity: AlertSeverityType
  type?: AlertType
  message?: string
  /** Denser padding and type for tight dashboard tiles */
  compact?: boolean
}

export const alertSeverityConfig = {
  critical: {
    label: "Critical",
    iconColor: "red",
    backgroundColor: "#FED7D7", // Red
    textColor: "primary",
    icon: FaCircleExclamation,
  },
  warning: {
    label: "Warning",
    iconColor: "orange",
    backgroundColor: "#FEEBC8", // Orange
    textColor: "primary",
    icon: FaTriangleExclamation,
  },
  info: {
    label: "Info",
    iconColor: "blue",
    backgroundColor: "#BEE3F8", // Blue
    textColor: "primary",
    icon: FaCircleInfo,
  },
  ok: {
    label: "No alerts",
    iconColor: "gray",
    backgroundColor: "#C6F6D5", // Green
    textColor: "primary",
    icon: FaCheck,
  }
}

export const AlertSeverityOrder: AlertSeverityType[] = [
  "critical",
  "warning",
  "info",
  "ok",
]

export const getAlertSeverityColor = (severity: AlertSeverityType | undefined, backgroundColor: boolean = false) => {
  const config = severity ? alertSeverityConfig[severity] : alertSeverityConfig.ok
  return backgroundColor ? config.backgroundColor : config.iconColor
}

export const getAlertSeverityHighest = (severities: (AlertSeverityType | string)[] | undefined) => {
  if (!severities) return undefined
  return AlertSeverityOrder.find((severity) => severities.includes(severity))
}

/** ISO datetime pattern: matches full ISO 8601 timestamps (with optional time, fractional seconds, Z or ±offset) and bare YYYY-MM-DD dates */
const ISO_DATETIME_PATTERN = /(\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?)/g

/**
 * Parse ISO date/datetime strings in an alert message and convert them
 * to the user's IANA timezone for display.
 */
export function formatAlertMessage(message: string): string {
  return message.replace(ISO_DATETIME_PATTERN, (match) => {
    try {
      const dt = toUserTZ(match)
      // If the original string included a time component, show date + time
      if (match.includes("T")) {
        return dt.format("dd MMM yyyy, HH:mm")
      }
      return dt.format("dd MMM yyyy")
    } catch {
      return match
    }
  })
}

export function AlertSeverityBadge({ severity, type = "status", message, compact }: AlertSeverityBadgeProps) {
  const config = alertSeverityConfig[severity]
  const Icon = config.icon
  const iconPx = compact ? 10 : 12
  const displayType = type === "pre_emptive" ? "pre-emptive" : type.replace(/_/g, " ")
  const displayMessage = message ? formatAlertMessage(message) : undefined

  const badge = (
    <Badge
      backgroundColor={config.backgroundColor}
      color={config.textColor}
      variant="subtle"
      display="inline-flex"
      alignItems="center"
      gap={compact ? 0.5 : 1}
      fontSize={compact ? "xs" : undefined}
      px={compact ? 1.5 : undefined}
      py={compact ? 0.5 : undefined}
      maxWidth={"100%"}
    >
      <Icon style={{ width: `${iconPx}px`, height: `${iconPx}px` }} color="primary" />
      <Text truncate minW={0}> {config.label}{severity !== "ok" && ` ${displayType} alert`}{displayMessage && ": "}</Text>
      {displayMessage && <Text textWrap={"nowrap"} truncate>{displayMessage}</Text>}
    </Badge>
  )

  if (displayMessage) {
    return (
      <Tooltip content={displayMessage} showArrow>
        {badge}
      </Tooltip>
    )
  }

  return badge
}

export default AlertSeverityBadge
